import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import { H3, H4 } from '@/components/Element/Heading';
import FullWidthFormSection from '@/components/LeadForm/FullWidthFormSection';
import InstallerReviewList from '@/components/Review/InstallerReviewList';
import TopCardInstaller from '@/components/TopCard/TopCardInstaller';
import { getReviews } from 'data/database';
import { Company } from 'data/types';
import { MONTH, YEAR } from 'lib/constants';
import Link from 'next/link';
import { installerTopCards, states } from 'data/static';
import { useState, useRef, useEffect } from 'react';
import { AiOutlineLeft, AiOutlineRight } from 'react-icons/ai';
import StateButton from '@/components/StateButton/StateButton';
import ReviewHeaderSection from '@/components/StateHeaderSection/ReviewHeaderSection';
import ReviewSearch from '@/components/Search/ReviewSearch';
import TableOfContents from '@/components/TableOfContents/TableOfContents';
import MetaTags from '@/components/Head/MetaTags';
import DrawerLink from '@/components/Button/DrawerLink';
import { StaticImageData } from 'next/image';
import { useRouter } from 'next/router';
// import { FaArrowRight } from 'react-icons/fa';
import AuthorBlockAutoDate from '@/components/AuthorBlock/AuthorBlockAutoDate';
import Accordion from '@/components/Accordion/Accordion';
// import { states as installerStates } from 'data/installers';
import { states as stateData } from 'data/static';

export async function getStaticProps() {
  const companies = await getReviews();
  return { props: { companies } };
}

type Props = {
  companies: Array<Company>;
};

const Page = ({ companies }: Props) => {
  const TOC = [
    {
      name: 'Choosing The Best Solar Installer For Your Home',
      id: 'choosing-the-best-solar-installer-for-your-home',
    },
    {
      name: 'How Do I Know Who The Best Solar Installer Is?',
      id: 'how-do-i-know-who-the-best-solar-installer-is',
    },
    {
      name: 'Finding The Best Solar Installer For Your Home',
      id: 'finding-the-best-installer',
    },
    {
      name: 'Warranty and After-Sales Support',
      id: 'warranty-and-after-sales-support',
    },
    {
      name: 'Local Installers, Tax Credits, And Rebates In Your State',
      id: 'local-installers-tax-credits-and-rebates-in-your-state',
    },
    {
      name: `FAQ's`,
      id: 'faq',
    },
  ];
  return (
    <>
      <MetaTags
        title={`Solar Installer Reviews`}
        description={`The best solar installer reviews in ${YEAR}. Compare our top solar installer picks + insights from real customers and find the best solar installer for your energy needs.`}
      />
      <ReviewHeaderSection
        count="4"
        dark
        heading="Home Solar Installer Reviews"
        subHeading="Compare Installers Near You"
      />
      <div className="hidden md:block">
        <FullWidthBarBreadcrumbs
          pages={[
            {
              name: 'Resources',
              href: '/solar-customer-resources',
            },
            {
              name: 'Solar Installer Reviews',
              href: '/solar-installer-reviews',
            },
          ]}
        />
      </div>
      <CenteredContentSection>
        <AuthorBlockAutoDate />

        <p>
          <strong>Who is the best solar installer for me and my home?</strong>{' '}
          More and more homeowners are switching to solar energy to cut down on
          their monthly energy expenses. But this environmentally friendly
          energy solution has its own unique challenges. From choosing the right
          installer and equipment to maximizing incentives making the right
          decision can be difficult. We&apos;re here to help.
        </p>
        <p>
          Here&apos;s a ranking of the best solar installers in {MONTH} {YEAR}{' '}
          and their reviews along with valuable advice for your solar shopping
          journey.
        </p>
        <H3>4 Best Solar Installers of {YEAR}</H3>
        <SolarCompaniesTable />
      </CenteredContentSection>
      <span id="CompanyCard" />
      <InstallerContainer installerTopCards={installerTopCards} />

      <CenteredContentSection>
        <Accordion
          title="Top Solar Installers By State"
          content={
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stateData.map((state) => (
                <StateButton
                  key={state.state}
                  image={state.image}
                  state={state.state}
                  link={`/solar-installer-reviews/best-home-solar-installers/${state.state
                    .toLowerCase()
                    .replace(/\s+/g, '-')}`}
                />
              ))}
            </div>
          }
        />
        <H3 className="capitalize">Search all solar installer reviews</H3>
        <p>
          Browse our directory to find a comprehensive list of installers and
          their reviews.
        </p>
        <ReviewSearch />
        <div>
          <InstallerReviewList companies={companies} />
        </div>
      </CenteredContentSection>
      <div id="form">
        <FullWidthFormSection
          cid={'plsxavozg3kbx1rj'}
          title="Find The Best Deals And Installers Near You"
        />
      </div>
      <CenteredContentSection>
        <TableOfContents sections={TOC} />
        <H3 id="choosing-the-best-solar-installer-for-your-home">
          Choosing The Best Solar Installer For Your Home
        </H3>
        <p>
          There are a few key considerations for homeowners looking to switch to
          solar energy. Below we breakdown some of the most important ones to be
          aware of:
        </p>
        <H4>Your Energy Needs:</H4>
        <p>
          Assess your current energy consumption to determine the size of the
          solar system you need. The higher your energy usage, the larger the
          system required to offset electricity costs effectively.
        </p>
        <H4>Your Location:</H4>
        <p>
          Your geographical location significantly impacts solar energy
          production. Consider the local climate, average sunlight hours, and
          shading from nearby buildings or trees when evaluating solar viability
          for your home.
        </p>
        <H4>Roof Size and Orientation:</H4>
        <p>
          The size and orientation of your roof are crucial factors. A larger,
          unobstructed south-facing roof is ideal for maximizing solar panel
          efficiency, as it receives more sunlight throughout the day.
        </p>
        <H4>Solar Panel Efficiency:</H4>
        <p>
          Different types of solar panels offer varying efficiency levels.
          High-efficiency panels generate more power in less space, making them
          suitable for smaller roofs or homeowners seeking maximum energy
          production.
        </p>
        <p>
          Do You Have Questions About Home Solar Products, Prices and Processes?
          Check out our <Link href="/home-solar-guide">Home Solar Guide.</Link>
        </p>
        <H4>Battery Storage:</H4>
        <p>
          If you want to store excess energy for nighttime or cloudy days,
          consider{' '}
          <Link href="/best-home-battery-systems">
            battery storage solutions
          </Link>
          . While batteries require an initial investment, they enhance energy
          self-sufficiency and can provide backup power during outages.
        </p>
        <H4>Future Energy Needs:</H4>
        <p>
          Think about your future energy needs. If your household is likely to
          grow or your energy consumption will increase due to new appliances or
          electric vehicles, consider a slightly larger solar system to
          accommodate future demands.
        </p>
        <H4 id="warranty-and-after-sales-support">Warranty Considerations:</H4>
        <p>
          Check the warranties offered for both solar panels and battery
          systems. Warranty length and coverage can provide peace of mind and
          protect your investment. Ask to understand what is covered and for how
          long.
        </p>
        <H4>Warranty Options:</H4>
        <p>
          Understand the warranties available for your solar system. Here are
          the main ones to know:
        </p>
        <ul>
          <li>
            <strong>Panel Warranties:</strong> Solar panels typically come with
            a 25-30 year warranty, covering product and energy production.
          </li>
          <li>
            <strong>Inverter Warranties:</strong>{' '}
            <Link href="/best-solar-inverters-for-homes">Inverters</Link> have
            shorter warranties, usually around 10-15 years. Consider extended
            warranties for added protection.
          </li>
          <li>
            <strong>Battery Warranties:</strong> If you opt for battery storage,
            inquire about the warranty for the battery unit.
          </li>
          <li>
            <strong>Workmanship Warranty:</strong> The installer may provide a
            warranty on the installation work, typically lasting 5-10 years.
          </li>
          <li>
            <strong>Performance Guarantees:</strong> Some installers offer
            performance guarantees ensuring a certain level of energy
            production.
          </li>
        </ul>

        <H4>Incentives and Tax Credits:</H4>
        <p>
          Research available{' '}
          <Link href={'/home-battery-solar-installers-near-me'}>
            incentives, tax credits, and rebates
          </Link>{' '}
          in your area. Government incentives can significantly reduce the
          upfront cost of solar installation. These incentives may include
          federal tax credits, state rebates, and local incentives. Make sure to
          take advantage of these opportunities to maximize your savings.
        </p>

        <H4>Payment Options:</H4>
        <p>
          There are various{' '}
          <Link href={'/solar-financing-lease-loan'}>
            solar payment options
          </Link>{' '}
          to choose from, including:
        </p>
        <ul>
          <li>
            <strong>Purchase:</strong> Buying the system outright provides
            long-term savings, as you own the panels and benefit from reduced
            energy bills.
          </li>
          <li>
            <strong>Leasing:</strong> Leasing allows you to use the solar system
            without the upfront cost, but you don&apos;t own the panels.
          </li>
          <li>
            <strong>Power Purchase Agreements (PPAs):</strong> With a PPA, you
            pay for the electricity the system generates, often at a lower rate
            than your local utility.
          </li>
          <li>
            <strong>Financing:</strong> Financing options, such as solar loans,
            enable you to spread the cost of the system over time.
          </li>
        </ul>

        <p>
          Want to know the best deal for your and your home?{' '}
          <Link href={'#form'}>Connect with us.</Link>
        </p>
        <H3 id="how-do-i-know-who-the-best-solar-installer-is">
          How Do I Know Who The Best Solar Installer Is?
        </H3>
        <p>
          The best solar installer possesses several key qualities and
          characteristics that distinguish them in the industry. When evaluating
          solar installers, consider the following factors to determine the best
          one for your needs:
        </p>
        <p>
          <strong>Experience and Track Record:</strong> Look for an installer
          with a proven track record of successful solar installations.
          Experience indicates expertise and reliability.
        </p>
        <p>
          <strong>Certifications and Licensing:</strong> Ensure that the
          installer is properly certified and licensed according to local
          regulations. Certification from organizations like the North American
          Board of Certified Energy Practitioners (NABCEP) is a valuable
          credential.
        </p>
        <p>
          <strong>Reputation and Customer Reviews:</strong> Read customer
          reviews and seek recommendations from friends, family, or neighbors
          who have worked with the installer. A strong reputation and positive
          feedback are indicators of customer satisfaction.
        </p>
        <p>
          <strong>Local Knowledge:</strong> A solar installer familiar with
          local permitting requirements, weather conditions, and utility
          policies can streamline the installation process and help you navigate
          local regulations.
        </p>
        <p>
          <strong>Customization:</strong> The best installer should be willing
          to tailor the solar system to meet your specific energy needs, budget,
          and preferences. They should conduct a thorough assessment of your
          home&apos;s energy usage and design a system accordingly.
        </p>
        <p>
          <strong>Quality of Components:</strong> Inquire about the quality of
          the solar panels, inverters, and other components they use.
          High-quality equipment typically ensures better long-term performance
          and durability.
        </p>
        <p>
          <strong>Transparent Pricing:</strong> A reputable installer should
          provide clear and transparent pricing. They should explain the costs
          involved and any potential financing options.
        </p>
        <p>
          <strong>Warranty and Support:</strong> Check the warranty options
          offered by the installer. This includes warranties for the solar
          panels, inverters, workmanship, and any additional components like
          batteries. Ensure they offer maintenance and support services.
        </p>
        <p>
          <strong>Safety Practices:</strong> Safety is paramount in solar
          installations. Ask about the installer&apos;s safety practices and
          protocols to protect both their workers and your property during the
          installation process.
        </p>
        <p>
          <strong>References and Portfolio:</strong> Request references from
          previous customers and ask to see examples of their past
          installations. This can provide insights into the quality of their
          work.
        </p>
        <p>
          <strong>Financial Stability:</strong> Assess the financial stability
          of the installer. You want to ensure they will be around for the long
          term to honor warranties and provide support.
        </p>
        <p>
          <strong>Communication and Responsiveness:</strong> A good installer
          should be responsive to your inquiries and communicate effectively
          throughout the installation process. They should be willing to address
          your questions and concerns.
        </p>
        <H3 id="local-installers-tax-credits-and-rebates-in-your-state">
          Local Installers, Tax Credits, And Rebates In Your State
        </H3>
        <Accordion
          title="Local Installers, Tax Credits, And Rebates In Your State"
          content={
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {states &&
                states.map((state, index: number) => (
                  <StateButton
                    key={index}
                    image={state.image}
                    state={state.state}
                    link={state.link}
                  />
                ))}
            </div>
          }
        />

        <CenteredContentSection>
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Compare Solar Companies By State
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4"></div>
          </div>
        </CenteredContentSection>

        <H3 id="faq">FAQs About Solar Installers in {YEAR}:</H3>
        <H4>What does a solar installer do?</H4>
        <p>
          Solar installers are professionals responsible for the installation,
          maintenance, and sometimes design of solar energy systems. They ensure
          that solar panels and associated components are properly installed and
          functioning efficiently.
        </p>
        <H4>
          Do I need a professional solar installer, or can I install solar
          panels myself?
        </H4>
        <p>
          While some people choose to install solar panels themselves (DIY
          solar), it&apos;s recommended to hire a professional installer. They
          have the knowledge, experience, and equipment to ensure a safe and
          efficient installation. DIY installations can be complex and may not
          be eligible for incentives or warranties.
        </p>
        <H4>How do I choose the right solar installer?</H4>
        <p>
          Research and compare solar installers in your area. Look for licensed
          and insured companies with a good reputation, customer reviews, and a
          track record of successful installations. Get multiple quotes and ask
          for references. <Link href={'#form'}>Connect with us</Link> for help.
        </p>
        <H4>What qualifications should I look for in a solar installer?</H4>
        <p>
          Ensure that the installer is licensed and certified in your region.
          They should have experience with the type of solar system you want
          (residential or commercial) and be knowledgeable about local
          permitting and regulations.
        </p>
        <H4>What type of solar panels should I choose?</H4>
        <p>
          Solar installers can provide guidance on panel selection based on your
          energy needs, budget, and location. They may offer different panel
          options, such as monocrystalline or polycrystalline, and discuss the
          benefits of each. Learn more about the{' '}
          <Link href={'/best-solar-panels-for-homes'}>
            best solar panel options.
          </Link>
        </p>
        <H4>How long does a solar installation typically take?</H4>
        <p>
          The{' '}
          <Link
            href={'/solar-faq/how-long-does-it-take-to-install-solar-panels'}
          >
            solar installation timeline
          </Link>{' '}
          can vary depending on factors like system size, complexity, and
          weather conditions. From signed contract to installation the whole
          process can take two to six months to complete. On average,
          residential installations typically take one day but some larger
          installers take multiple days.
        </p>
        <H4>
          What permits and paperwork are required for a solar installation?
        </H4>
        <p>
          Solar installers often handle the permitting process, which includes
          obtaining necessary approvals from local authorities. They should also
          help with any rebate or incentive applications.
        </p>
        <H4>
          What warranties and maintenance services do solar installers offer?
        </H4>
        <p>
          Solar installers typically offer warranties on the installation work
          and may facilitate manufacturer warranties on the solar panels and
          inverters. They may also provide maintenance and repair services to
          ensure your system continues to operate efficiently.
        </p>
        <H4>
          How much does solar installation cost, and what financing options are
          available?
        </H4>
        <p>
          The typical cost per watt is $2.50-$3.50. But{' '}
          <Link href={'/solar-faq/how-much-do-solar-panels-cost'}>
            solar installation costs
          </Link>{' '}
          vary depending on the size and complexity of the system. Solar
          installers can provide cost estimates and discuss financing options,
          such as purchase, leasing, or power purchase agreements (PPAs).
        </p>
        <H4>What maintenance is required for a solar system, and how often?</H4>
        <p>
          Solar panels are generally low-maintenance, but they may require
          occasional cleaning and inspection. Solar installers can provide
          guidance on maintenance tasks and schedules.
        </p>
        <H4>
          What happens if my solar panels produce more electricity than I use?
        </H4>
        <p>
          Excess energy can often be fed back into the grid, depending on your
          location and utility policies. Your solar installer can explain how
          net metering works and how you can benefit from it.
        </p>
        <H3 id="finding-the-best-installer">
          Finding The Best Solar Installer For Your Home
        </H3>
        <p>
          Selecting the right solar installer is a crucial step in your solar
          journey.
        </p>
        <p>
          It involves considering factors such as experience, certifications,
          reputation, customization options, equipment quality, pricing
          transparency, warranty offerings, safety practices, and communication.
          By evaluating these aspects and comparing installers, you can make an
          informed decision that ensures a successful solar installation
          tailored to your unique needs and preferences.
        </p>
        <p>
          With all these choices to make, choosing the right solar energy
          solution can be overwhelming. That&apos;s why we provide homeowners
          with better energy products, better value, better pricing, and an
          overall better shopping experience.
        </p>
        <p>
          We believe in honesty and transparency. No hidden agendas here.{' '}
          <strong>
            EnergyPal is your #1 source for unbiased insights into home solar.
          </strong>
        </p>
        <p>
          Our platform offers industry-leading solar installers, energy products
          and pricing. With decades of experience, our team has a deep
          understanding of solar power, home energy use, utility rates, EV
          charging, and battery storage.
        </p>
        <p>
          When you choose EnergyPal, you choose reliability. We&apos;re here to
          advocate for you, ensuring your solar project is done right. Our
          commitment to customer trust sets us apart.
        </p>
        <p>
          And guess what?{' '}
          <strong>You&apos;ll save more and get more with EnergyPal.</strong>{' '}
          Our friendly Energy Advisors will find the best solar energy package
          for your home, tailored just for you.
        </p>
        <p>So, are you ready to go solar, for less?</p>
      </CenteredContentSection>
      <div id="form2">
        <FullWidthFormSection
          cid={'plsxavozg3kbx1rj'}
          title="Find The Best Deals And Installers Near You"
        />
      </div>
    </>
  );
};
export default Page;

type InstallerData = {
  name: string;

  imageSrc: StaticImageData; // You may want to specify the correct data type for image source
  rating: string;
  reasonsWhy: string[];
  installerStrengths: string[];
  concerns: string[];
  reviews: {
    name: string;
    content: string;
    rating: string;
    date: string;
    location: string;
  }[];
  link: string;
  headquarters: string;
  serviceArea: string;
};

interface InstallerContainerProps {
  installerTopCards: InstallerData[];
}

const InstallerContainer: React.FC<InstallerContainerProps> = ({
  installerTopCards,
}) => {
  const scrollContainer = useRef<HTMLDivElement | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalCards = installerTopCards.length;
  const router = useRouter();
  const installerId = router.query.installerId;

  const handleScroll = () => {
    if (scrollContainer.current && scrollContainer.current.firstChild) {
      const child = scrollContainer.current.firstChild as HTMLElement;
      const itemWidth = child.offsetWidth;
      const index = Math.round(scrollContainer.current.scrollLeft / itemWidth);
      setCurrentIndex(Math.max(index, 0));
    }
  };

  useEffect(() => {
    // Ensure installerId is a string and parse it to an integer, fallback to 0 if not a string or if parsing fails
    const parsedInstallerId =
      typeof installerId === 'string' ? parseInt(installerId, 10) : 0;
    // Use isNaN to check if parsedInstallerId is NaN. If so, fallback to 0.
    console.log(installerId);

    const index = isNaN(parsedInstallerId) ? 0 : parsedInstallerId;
    console.log(index);

    setCurrentIndex(index);
    scrollToCard(index);

    if (scrollContainer.current) {
      scrollContainer.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollContainer.current) {
        scrollContainer.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [installerId]); // Add installerId as a dependency to re-run the effect if it changes

  const scrollToCard = (index: number) => {
    if (scrollContainer.current && scrollContainer.current.firstChild) {
      const child = scrollContainer.current.firstChild as HTMLElement;
      const itemWidth = child.offsetWidth;
      scrollContainer.current.scrollTo({
        left: itemWidth * index,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="max-w-3xl md:mx-auto mx-2 relative">
      {/* Top indicators */}
      <div className="flex justify-center mt-2 items-center">
        {currentIndex > 0 ? (
          <button
            className="arrow-button left-arrow text-3xl  mr-2 pb-2 block md:hidden"
            onClick={scrollToCard.bind(null, Math.max(currentIndex - 1, 0))}
          >
            <AiOutlineLeft />
          </button>
        ) : (
          <div className="w-[30px] h-[36px] mr-2 pb-2 block md:hidden" />
        )}
        {Array.from({ length: totalCards }).map((_, i) => (
          <div
            key={i}
            className={`h-3 w-8 mb-2 rounded-full cursor-pointer ${
              i === currentIndex ? 'bg-primary' : 'bg-gray-300'
            } mx-1`}
            onClick={() => scrollToCard(i)}
          ></div>
        ))}
        {currentIndex < 5 ? (
          <button
            className="arrow-button right-arrow text-3xl ml-2 pb-2 block md:hidden"
            onClick={scrollToCard.bind(
              null,
              Math.min(currentIndex + 1, totalCards - 1)
            )}
          >
            <AiOutlineRight />
          </button>
        ) : (
          <div className="w-[30px] h-[36px] ml-2 pb-2 block md:hidden" />
        )}
      </div>

      {/* Existing content */}
      <div className="flex flex-row">
        <button
          className="arrow-button left-arrow text-3xl hidden md:block"
          onClick={scrollToCard.bind(null, Math.max(currentIndex - 1, 0))}
        >
          <AiOutlineLeft />
        </button>
        <div
          className="flex space-x-4 overflow-x-scroll"
          style={{
            scrollSnapType: 'x mandatory',
          }}
          ref={scrollContainer}
        >
          {installerTopCards.map((installerData, index) => (
            <div
              key={index}
              className="min-w-fit"
              style={{
                scrollSnapAlign: 'center',
              }}
            >
              {/* Card content */}
              <TopCardInstaller installerData={installerData} />
            </div>
          ))}
        </div>
        <button
          className="arrow-button right-arrow text-3xl hidden md:block"
          onClick={scrollToCard.bind(
            null,
            Math.min(currentIndex + 1, totalCards - 1)
          )}
        >
          <AiOutlineRight />
        </button>
      </div>
      {/* Bottom indicators */}
      <div className="flex justify-center mt-2 items-center">
        {currentIndex > 0 ? (
          <button
            className="arrow-button left-arrow text-3xl  mr-2 pb-2 block md:hidden"
            onClick={scrollToCard.bind(null, Math.max(currentIndex - 1, 0))}
          >
            <AiOutlineLeft />
          </button>
        ) : (
          <div className="w-[30px] h-[36px] mr-2 pb-2 block md:hidden" />
        )}
        {Array.from({ length: totalCards }).map((_, i) => (
          <div
            key={i}
            className={`h-3 w-8 mb-2 rounded-full cursor-pointer ${
              i === currentIndex ? 'bg-primary' : 'bg-gray-300'
            } mx-1`}
            onClick={() => scrollToCard(i)}
          ></div>
        ))}
        {currentIndex < 5 ? (
          <button
            className="arrow-button right-arrow text-3xl ml-2 pb-2 block md:hidden"
            onClick={scrollToCard.bind(
              null,
              Math.min(currentIndex + 1, totalCards - 1)
            )}
          >
            <AiOutlineRight />
          </button>
        ) : (
          <div className="w-[30px] h-[36px] ml-2 pb-2 block md:hidden" />
        )}
      </div>
    </div>
  );
};

const SolarCompaniesTable = () => {
  const companies = [
    // {
    //   name: 'SunPower',
    //   knownFor: 'Product quality and complete warranties',
    //   states: 40,
    //   purchaseOptions: ['Lease', 'PPA', 'Loan', 'Cash'],
    //   workmanshipWarranty: '25 Years',
    //   equipmentWarranty: '25 Years',
    //   rating: '4.6/5',
    // },
    {
      name: 'Sunnova Solar',
      knownFor: 'Incredible installation and battery warranties',
      states: 18,
      purchaseOptions: ['Lease', 'PPA', 'Loan', 'Cash'],
      workmanshipWarranty: '25 Years',
      equipmentWarranty: '25 Years',
      rating: '4.2/5',
    },
    {
      name: 'Axia by Qcells',
      knownFor: 'Great installation pricing and warranties',
      states: 3,
      purchaseOptions: ['Lease', 'PPA', 'Loan', 'Cash'],
      workmanshipWarranty: '25 Years',
      equipmentWarranty: '25 Years',
      rating: '4.1/5',
    },
    {
      name: 'Blue Raven Solar',
      knownFor: 'Reputable company with strong project management expertise',
      states: 18,
      purchaseOptions: ['Lease', 'PPA', 'Loan', 'Cash'],
      workmanshipWarranty: '10 Years',
      equipmentWarranty: '25 Years',
      rating: '4.47/5',
    },
    // {
    //   name: 'Palmetto',
    //   knownFor: 'Exceptional service and installation pricing',
    //   states: 20,
    //   purchaseOptions: ['Lease', 'PPA', 'Loan', 'Cash'],
    //   workmanshipWarranty: '10 Years',
    //   equipmentWarranty: '25 Years',
    //   rating: '3.9/5',
    // },
    {
      name: 'Suntuity Solar',
      knownFor: 'Thorough offerings with many years of experience',
      states: 21,
      purchaseOptions: ['Lease', 'PPA', 'Loan', 'Cash'],
      workmanshipWarranty: '10 Years',
      equipmentWarranty: '25 Years',
      rating: '3.8/5',
    },
  ];
  return (
    <div className="overflow-x-auto rounded-md">
      <table className="w-full bg-white divide-y divide-gray-200 text-sm rounded-md">
        <thead>
          <tr>
            <th className="p-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
            {companies.map((company) => (
              <th
                key={company.name}
                className="p-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider "
              >
                {company.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className=" p-2  text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:text-sm">
              Known for
            </td>
            {companies.map((company) => (
              <td key={company.name} className="p-2 text-sm">
                {company.knownFor}
              </td>
            ))}
          </tr>
          <tr>
            <td className=" p-2  text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:text-sm">
              States
            </td>
            {companies.map((company) => (
              <td key={company.name} className="p-2">
                {company.states}
              </td>
            ))}
          </tr>
          <tr>
            <td className=" p-2  text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:text-sm">
              Purchase Options
            </td>
            {companies.map((company) => (
              <td key={company.name} className="p-2">
                {company.purchaseOptions.join(', ')}
              </td>
            ))}
          </tr>
          <tr>
            <td className=" p-2  text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:text-sm">
              Workmanship Warranty
            </td>
            {companies.map((company) => (
              <td key={company.name} className="p-2">
                {company.workmanshipWarranty}
              </td>
            ))}
          </tr>
          <tr>
            <td className="p-2  text-left text-xs font-medium text-gray-500 uppercase tracking-wider  md:text-sm">
              Equipment Warranty
            </td>
            {companies.map((company) => (
              <td key={company.name} className="p-2">
                {company.equipmentWarranty}
              </td>
            ))}
          </tr>
          <tr>
            <td className=" p-2  text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:text-sm">
              Rating
            </td>
            {companies.map((company) => (
              <td key={company.name} className="p-2">
                {company.rating}
              </td>
            ))}
          </tr>
          <tr>
            <td className="p-2 font-bold text-sm"></td>
            {companies.map((company) => (
              <td key={company.name} className="bg-primary border-2">
                <DrawerLink className=" no-underline text-white hover:cursor-pointer">
                  <div className="w-18 mr-1 bg-primary ">
                    <h3 className="text-sm no-underline text-white m-0 p-0 text-left whitespace-nowrap">
                      Free Quote
                    </h3>
                  </div>
                </DrawerLink>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};
